import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/solid';
import { GiftChoiceUnitData } from '@shared/api/giftChoiceUnit/giftChoiceUnit.interface';
import {
  giftCouponByCodeDataAtomFamily,
  giftCouponByCodeStatusAtomFamily,
} from '@shared/api/giftCoupon/giftCoupon.atom';
import {
  requestCouponRedemption,
  requestExtraRedeem,
  saveExtraRedeemLater,
  saveRecipient,
  viewCoupon,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import { useGiftCouponByCodeHook } from '@shared/api/giftCoupon/giftCoupon.hook';
import { GiftCouponRequestDto } from '@shared/api/giftCoupon/giftCoupon.interface';
import { Loading } from '@shared/components/Loading';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { AxiosError } from 'axios';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ContactEmailLink } from '../components/ContactEmailLink';
import { giftStoreNavBarTitleAtom } from '../containers/GiftStoreNavBar/giftStoreNavBar.atom';
import CouponStatusView from './giftCouponRedeem/CouponStatusView';
import { ExchangeCautionItems } from './giftCouponRedeem/ExchangeCautionNotice';
import {
  RESTRICTED_PHONE_ERROR_MESSAGE,
  formatPhoneNumber,
  getNextCouponSendSlot,
  getRedeemStatusMessage,
  getSpawnMapping,
  hasCouponApplied,
  isPhoneAllowedByRestrictedPhone,
  markCouponApplied,
  setSpawnMapping,
} from './giftCouponRedeem/giftCouponRedeem.utils';
import PhoneVerificationSection from './giftCouponRedeem/PhoneVerificationSection';
import ProductImageCarousel, {
  CarouselImage,
} from './giftCouponRedeem/ProductImageCarousel';
import SaveLinkView from './giftCouponRedeem/SaveLinkView';
import SignatureModal from './giftCouponRedeem/SignatureModal';
import SubmitSuccessView from './giftCouponRedeem/SubmitSuccessView';
import ValidPeriodConsentModal from './giftCouponRedeem/ValidPeriodConsentModal';

const GiftCouponRedeemPage: FC = function GiftCouponRedeemPage() {
  const { couponCode } = useParams<{ couponCode: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminPreview =
    searchParams.get('secretKey') === process.env.REACT_APP_GIFT_STORE_ADMIN_PREVIEW_KEY;

  // 현재 경로의 마지막 세그먼트(쿠폰코드)를 다른 코드로 바꾼 경로 (도메인 prefix 무관)
  const toCouponPath = useCallback(
    (code: string) => {
      const parts = location.pathname.split('/');
      parts[parts.length - 1] = code;
      return parts.join('/');
    },
    [location.pathname],
  );

  const setTitle = useSetRecoilState(giftStoreNavBarTitleAtom);

  // 폼 상태
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedChoiceUnit, setSelectedChoiceUnit] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [redeemSubmitMeta, setRedeemSubmitMeta] = useState<{
    submittedAt: Date;
    expectedSendAt: Date;
  } | null>(null);
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);
  const [isSaveLinkView, setIsSaveLinkView] = useState(false);
  const [isSavingLater, setIsSavingLater] = useState(false);
  const [isPreset, setIsPreset] = useState(false);
  // 추가교환 나중에받기 → 발급된 본인 spawn 쿠폰의 저장 링크 정보 (있으면 SaveLinkView가 이걸 사용)
  const [saveLink, setSaveLink] = useState<{
    url: string;
    code: string;
    expiresAt?: Date | string;
  } | null>(null);

  // 쿠폰 조회
  useGiftCouponByCodeHook(couponCode ?? '');
  const status = useRecoilValue(giftCouponByCodeStatusAtomFamily(couponCode ?? ''));
  const coupon = useRecoilValue(giftCouponByCodeDataAtomFamily(couponCode ?? ''));

  // 신청 성공 화면에 표시할 코드 (추가신청 시 spawn된 새 쿠폰 코드)
  const [successCouponCode, setSuccessCouponCode] = useState<string | null>(null);

  // 추가교환(중복 공유 쿠폰) 모드 판정.
  // 이미 처리된(교환신청/발송) 쿠폰이라도, extraRedeem이 열려있고(잔여>0·미만료)
  // 이 기기에서 신청 기록이 없는 "신규 유입자"이면 일반 신청 폼을 노출한다.
  const extraRedeem = coupon?.extraRedeem;
  const isExtraRedeemOpen =
    !!extraRedeem &&
    extraRedeem.allowed - extraRedeem.used > 0 &&
    (!extraRedeem.expiresAt || new Date(extraRedeem.expiresAt).getTime() > Date.now());
  const isExtraMode =
    isExtraRedeemOpen &&
    !!coupon &&
    getRedeemStatusMessage(coupon.redeemStatus) !== null &&
    // admin 미리보기는 localStorage와 무관하게 실제처럼 폼을 노출한다.
    // 실사용자는 이 기기에서 신청 기록(applied)이나 본인 spawn(map)이 없을 때만 노출.
    (isAdminPreview ||
      (!!couponCode && !hasCouponApplied(couponCode) && !getSpawnMapping(couponCode)));

  // 원본(중복노출) 쿠폰을 재방문했는데 이 기기에서 이미 본인 spawn 쿠폰을 발급받았다면
  // 본인 쿠폰 URL로 보낸다. (admin 미리보기 제외)
  const spawnRedirectCode =
    !isAdminPreview && coupon?.extraRedeem && couponCode
      ? getSpawnMapping(couponCode)
      : null;
  // 방금 나중에받기로 저장 화면(SaveLinkView)을 띄운 직후에는 리다이렉트하지 않는다.
  const pendingSpawnRedirect =
    !!spawnRedirectCode && spawnRedirectCode !== couponCode && !isSaveLinkView;

  useEffect(() => {
    if (pendingSpawnRedirect && spawnRedirectCode) {
      navigate(toCouponPath(spawnRedirectCode), { replace: true });
    }
  }, [pendingSpawnRedirect, spawnRedirectCode, navigate, toCouponPath]);

  useEffect(() => {
    setTitle('답례품 수령');
  }, [setTitle]);

  useEffect(() => {
    if (coupon && !isAdminPreview) {
      viewCoupon(coupon._id).catch(console.error);
    }
  }, [coupon, isAdminPreview]);

  // 이전 인증 정보가 있으면 프리셋 (추가신청자는 원본 수령자 정보를 프리셋하지 않음)
  useEffect(() => {
    if (!coupon) return;
    if (isExtraMode) return;
    if (coupon.verification?.phoneNumber && !phoneNumber) {
      setPhoneNumber(formatPhoneNumber(coupon.verification.phoneNumber));
      setIsVerified(true);
      setIsPreset(true);
    }
    if (
      coupon.selectedChoiceUnit &&
      typeof coupon.selectedChoiceUnit === 'object' &&
      !selectedChoiceUnit
    ) {
      setSelectedChoiceUnit(coupon.selectedChoiceUnit._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coupon]);

  // 상품 정보
  const giftItem = coupon?.giftItem;
  const giftItemInfo = typeof giftItem === 'object' ? giftItem?.giftItemInfo : null;

  const isSelectable = giftItemInfo?.isSelectable ?? false;
  const choiceUnits = useMemo(() => {
    return (giftItemInfo?.choiceUnits ?? []) as GiftChoiceUnitData[];
  }, [giftItemInfo]);

  const selectedUnit = useMemo(() => {
    if (!selectedChoiceUnit) return null;
    return choiceUnits.find((u) => u._id === selectedChoiceUnit) ?? null;
  }, [selectedChoiceUnit, choiceUnits]);

  const imageUrl = useMemo(() => {
    if (selectedUnit?.image?.url) return selectedUnit.image.url;
    if (typeof giftItem === 'object') {
      return giftItemInfo?.image?.url || giftItem?.image?.url || '';
    }
    return '';
  }, [giftItem, giftItemInfo, selectedUnit]);

  const brandName = useMemo(() => {
    if (selectedUnit?.giftItemBrand && typeof selectedUnit.giftItemBrand === 'object') {
      return selectedUnit.giftItemBrand.name;
    }
    if (giftItemInfo) {
      const brand = giftItemInfo.giftItemBrand;
      if (brand && typeof brand === 'object' && 'name' in brand) {
        return brand.name;
      }
    }
    return null;
  }, [giftItemInfo, selectedUnit]);

  const itemName =
    typeof giftItem === 'object' ? giftItem?.name || giftItemInfo?.name : '';

  const couponDetail = useMemo(() => {
    // 판매처(giftItemBrand)마다 유효기간이 달라, 선택형(옵션 선택됨)은 그 옵션이
    // 연결된 브랜드의 couponDetail을 최우선으로 쓴다. 쿠폰형(단일)은 기존대로
    // giftItemInfo.couponDetail. 필드 단위로 채워, 브랜드에 교환처만 없을 때
    // 하위 출처의 교환처가 유지되도록 한다.
    const brandCouponDetail =
      selectedUnit?.giftItemBrand && typeof selectedUnit.giftItemBrand === 'object'
        ? selectedUnit.giftItemBrand.couponDetail
        : undefined;
    const sources = [brandCouponDetail, selectedUnit?.couponDetail, giftItemInfo?.couponDetail];
    const validPeriod = sources.map((s) => s?.validPeriod).find(Boolean) ?? '';
    const placeOfExchange = sources.map((s) => s?.placeOfExchange).find(Boolean) ?? '';
    if (!validPeriod && !placeOfExchange) return undefined;
    return { validPeriod, placeOfExchange };
  }, [giftItemInfo, selectedUnit]);

  const phoneInputRef = useRef<HTMLDivElement>(null);

  // 캐로셀
  const [carouselIndex, setCarouselIndex] = useState(0);

  const mainImageUrl = useMemo(() => {
    if (typeof giftItem === 'object') {
      return giftItemInfo?.image?.url || giftItem?.image?.url || '';
    }
    return '';
  }, [giftItem, giftItemInfo]);

  const carouselImages: CarouselImage[] = useMemo(() => {
    if (!isSelectable || choiceUnits.length === 0) {
      return [{ url: mainImageUrl, name: itemName || '답례품', unitId: null, brandName }];
    }
    const mainSlide: CarouselImage = {
      url: mainImageUrl,
      name: itemName || '답례품',
      unitId: null,
      brandName,
    };
    const unitSlides: CarouselImage[] = choiceUnits.map((unit) => ({
      url: unit.image?.url || '',
      name: unit.name,
      unitId: unit._id,
      brandName:
        unit.giftItemBrand && typeof unit.giftItemBrand === 'object'
          ? unit.giftItemBrand.name
          : null,
    }));
    return [mainSlide, ...unitSlides];
  }, [isSelectable, choiceUnits, mainImageUrl, itemName, brandName]);

  useEffect(() => {
    if (!isSelectable) return;
    const unitId = carouselImages[carouselIndex]?.unitId ?? null;
    setSelectedChoiceUnit(unitId);
  }, [carouselIndex, isSelectable, carouselImages]);

  const handleSelectUnit = useCallback(
    (unitId: string | null) => {
      setSelectedChoiceUnit(unitId);
      if (unitId === null) {
        setCarouselIndex(0);
      } else {
        const idx = carouselImages.findIndex((item) => item.unitId === unitId);
        if (idx >= 0) setCarouselIndex(idx);
        setTimeout(() => {
          phoneInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 200);
      }
    },
    [carouselImages],
  );

  // "받기" 버튼 클릭 → 유효기간 안내 모달 열기
  const handleSubmit = useCallback(() => {
    const cleanPhone = phoneNumber.replace(/-/g, '');

    if (!coupon || !cleanPhone) {
      setSubmitError('휴대폰 번호를 입력해 주세요.');
      return;
    }

    if (!isVerified) {
      setSubmitError('번호 인증을 먼저 완료해 주세요.');
      return;
    }

    // 회수 잠금 / 사전 등록된 수령자 번호와 매칭되지 않으면 차단.
    // 본인 인증 발송 단계에서도 동일하게 검사하지만, 이전에 인증된 번호가 미리
    // 채워진(preset) 케이스에서는 발송을 거치지 않으므로 여기서 한 번 더 막아준다.
    // 추가신청(extra) 모드에서는 원본의 회수 잠금이 적용되지 않으며, 중복 수령
    // 차단은 백엔드(기존 수령자 번호 차단)가 담당한다.
    if (
      !isExtraMode &&
      !isPhoneAllowedByRestrictedPhone(cleanPhone, coupon.additionalRecipientPhone)
    ) {
      setSubmitError(RESTRICTED_PHONE_ERROR_MESSAGE);
      return;
    }

    if (isSelectable && !selectedChoiceUnit) {
      setSubmitError('교환할 상품을 선택해 주세요.');
      return;
    }

    setSubmitError(null);
    setIsConsentModalOpen(true);
  }, [coupon, phoneNumber, isVerified, isSelectable, selectedChoiceUnit, isExtraMode]);

  // 모달에서 "지금 받기" → 서명 모달 열기
  const handleConsentNow = useCallback(() => {
    setIsConsentModalOpen(false);
    setIsSignatureModalOpen(true);
  }, []);

  // 모달에서 "나중에 신청하기"
  // - 일반: saveRecipient로 원본 쿠폰에 번호 예약 → 링크 저장 화면
  // - 추가신청(extra): 본인 전용 spawn 쿠폰(Available) 발급 → 발급된 코드로 이동
  const handleConsentLater = useCallback(async () => {
    const cleanPhone = phoneNumber.replace(/-/g, '');
    if (!coupon) return;

    setIsSavingLater(true);

    try {
      if (isExtraMode) {
        const spawned = await saveExtraRedeemLater(coupon._id, {
          phoneNumber: cleanPhone,
          ...(selectedChoiceUnit ? { selectedChoiceUnit } : {}),
        });
        const spawnCode = spawned.code ?? spawned.fullCode;
        if (couponCode && !isAdminPreview) {
          markCouponApplied(couponCode);
          if (spawnCode) setSpawnMapping(couponCode, spawnCode);
        }
        // 발급된 본인 spawn 쿠폰 링크로 바로 저장 화면(SaveLinkView)을 띄운다.
        // (spawn URL로 navigate하면 받기 폼이 다시 떠서 한 번 더 눌러야 하는 문제 방지)
        if (spawnCode) {
          setSaveLink({
            url: `${window.location.origin}${toCouponPath(spawnCode)}`,
            code: spawnCode,
            expiresAt: spawned.expiresAt,
          });
        }
        setIsConsentModalOpen(false);
        setIsSaveLinkView(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      await saveRecipient(coupon._id, {
        phoneNumber: cleanPhone,
        ...(selectedChoiceUnit ? { selectedChoiceUnit } : {}),
      });
      if (couponCode && !isAdminPreview) markCouponApplied(couponCode);
      setIsConsentModalOpen(false);
      setIsSaveLinkView(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('수신자 저장 실패:', error);
      const serverMsg = (error as AxiosError<{ message?: string }>).response?.data
        ?.message;
      setSubmitError(serverMsg || '저장에 실패했습니다. 다시 시도해 주세요.');
      setIsConsentModalOpen(false);
    } finally {
      setIsSavingLater(false);
    }
  }, [
    coupon,
    phoneNumber,
    selectedChoiceUnit,
    isExtraMode,
    couponCode,
    isAdminPreview,
    toCouponPath,
  ]);

  // 서명 완료 후 실제 교환 신청
  const handleSubmitWithSignature = useCallback(
    async (signatureDataUrl: string) => {
      const cleanPhone = phoneNumber.replace(/-/g, '');
      if (!coupon) return;

      setIsSubmitting(true);
      setSubmitError(null);

      try {
        const dto: GiftCouponRequestDto = {
          name: '',
          phoneNumber: cleanPhone,
          signatureImage: signatureDataUrl,
        };
        if (selectedChoiceUnit) dto.selectedChoiceUnit = selectedChoiceUnit;

        if (isExtraMode) {
          // 추가신청 → 본인 전용 spawn 쿠폰(Requested) 발급. 발급된 코드로 이동시킨다.
          const spawned = await requestExtraRedeem(coupon._id, dto);
          const spawnCode = spawned.code ?? spawned.fullCode;
          if (couponCode && !isAdminPreview) {
            markCouponApplied(couponCode);
            if (spawnCode) setSpawnMapping(couponCode, spawnCode);
          }
          setIsSignatureModalOpen(false);
          if (spawnCode && !isAdminPreview) {
            navigate(toCouponPath(spawnCode), { replace: true });
            return;
          }
          // admin 미리보기 등: 이동 없이 성공 화면 폴백
          setSuccessCouponCode(spawnCode ?? null);
          setSubmitSuccess(true);
          return;
        }

        await requestCouponRedemption(coupon._id, dto);
        setSuccessCouponCode(couponCode ?? null);
        if (couponCode && !isAdminPreview) markCouponApplied(couponCode);
        const submittedAt = new Date();
        setRedeemSubmitMeta({
          submittedAt,
          expectedSendAt: getNextCouponSendSlot(submittedAt),
        });
        setIsSignatureModalOpen(false);
        setSubmitSuccess(true);
      } catch (error) {
        console.error('교환 신청 실패:', error);
        // 백엔드 400 메시지(추가신청 미오픈/인증 불일치/중복 수령 차단/허용 소진 등)를
        // 그대로 노출한다. axios 에러는 변환되지 않으므로 response.data.message에서 꺼낸다.
        const serverMsg = (error as AxiosError<{ message?: string }>).response?.data
          ?.message;
        setSubmitError(serverMsg || '교환 신청에 실패했습니다. 다시 시도해 주세요.');
        // 실패 시 서명창을 닫아야 에러 메시지가 가려지지 않고 바로 보인다.
        // (이미 신청한 번호 등으로 막힌 경우 "왜 안 되는지" 인지 가능)
        setIsSignatureModalOpen(false);
        setTimeout(() => {
          phoneInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      coupon,
      phoneNumber,
      selectedChoiceUnit,
      isExtraMode,
      couponCode,
      isAdminPreview,
      navigate,
      toCouponPath,
    ],
  );

  // --- 렌더링 분기 ---

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  if (!coupon) {
    return (
      <div className="center-box min-h-screen flex-col gap-4 px-4 text-center">
        <ExclamationCircleIcon className="h-16 w-16 text-gray-300" />
        <p className="text-gray-500">쿠폰을 찾을 수 없습니다.</p>
        <p className="text-sm text-gray-400">
          유효하지 않은 쿠폰 코드이거나 이미 사용된 쿠폰입니다.
        </p>
      </div>
    );
  }

  // 이 기기에서 이미 본인 spawn 쿠폰을 발급받은 원본 URL 재방문 — 본인 쿠폰으로 이동 중
  if (pendingSpawnRedirect) {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  const statusMessage = getRedeemStatusMessage(coupon.redeemStatus);

  // 나중에 신청하기 → 링크 저장 화면
  if (isSaveLinkView) {
    const saveLinkSelectedUnit = selectedChoiceUnit
      ? choiceUnits.find((u) => u._id === selectedChoiceUnit) ?? null
      : null;

    return (
      <SaveLinkView
        expiresAt={new Date(saveLink?.expiresAt ?? coupon.expiresAt)}
        linkUrl={saveLink?.url ?? window.location.href}
        imageUrl={imageUrl}
        itemName={itemName || ''}
        couponCode={saveLink?.code ?? couponCode ?? ''}
        brandName={brandName}
        selectedUnit={saveLinkSelectedUnit}
      />
    );
  }

  // 신청 직후 성공 화면
  if (submitSuccess) {
    const submitSelectedUnit = selectedChoiceUnit
      ? choiceUnits.find((u) => u._id === selectedChoiceUnit) ?? null
      : null;

    return (
      <SubmitSuccessView
        redeemSubmitMeta={redeemSubmitMeta}
        imageUrl={imageUrl}
        itemName={itemName || ''}
        couponCode={successCouponCode || couponCode || ''}
        brandName={brandName}
        selectedUnit={submitSelectedUnit}
        validPeriod={couponDetail?.validPeriod}
      />
    );
  }

  // 이미 처리된 쿠폰 — 단, 추가교환 모드(신규 유입자 + extraRedeem 열림)에서는
  // 아래 신청 폼으로 넘어간다.
  if (statusMessage && !isExtraMode) {
    return (
      <CouponStatusView
        coupon={coupon}
        statusMessage={statusMessage}
        imageUrl={imageUrl}
        itemName={itemName || ''}
        couponCode={couponCode || ''}
        brandName={brandName}
        validPeriod={couponDetail?.validPeriod}
      />
    );
  }

  const isMainSlide = carouselIndex === 0;
  const displayBrandName = isMainSlide ? brandName : null;

  // 교환 신청 폼
  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-100 to-white pb-8">
      <ProductImageCarousel
        images={carouselImages}
        carouselIndex={carouselIndex}
        onIndexChange={setCarouselIndex}
        displayBrandName={displayBrandName}
      />

      <div className="px-4 pt-5">
        {/* 상품명 */}
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="text-base font-medium text-stone-800">
            {selectedUnit?.name || itemName}
          </h2>
          <span className="flex-shrink-0 text-xs text-stone-500">{couponCode}</span>
        </div>

        {/* 유효기간 안내 */}
        <div className="mt-4 space-y-2">
          {coupon.expiresAt && (
            <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
              <div>
                <span className="text-sm font-medium text-amber-800">신청 기한</span>
                <p className="text-xs text-amber-600">이 링크로 신청 가능한 기한</p>
              </div>
              <span className="text-sm font-bold text-amber-800">
                ~
                {new Date(coupon.expiresAt).toLocaleDateString('ko-KR', {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                })}
              </span>
            </div>
          )}

          {couponDetail && (couponDetail.validPeriod || couponDetail.placeOfExchange) && (
            <div className="rounded-lg bg-stone-50 p-3">
              {couponDetail.validPeriod && (
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-bold text-stone-600">
                      상품 유효기간
                    </span>
                    <p className="text-xs font-medium text-stone-400">
                      발송된 쿠폰의 사용 기한
                    </p>
                  </div>
                  <span className="text-sm font-medium text-stone-700">
                    {couponDetail.validPeriod}
                  </span>
                </div>
              )}
              {couponDetail.placeOfExchange && (
                <div
                  className={`flex items-center justify-between gap-4 ${
                    couponDetail.validPeriod ? 'mt-2 border-t border-stone-200 pt-2' : ''
                  }`}
                >
                  <span className="whitespace-pre text-sm font-bold text-stone-500">
                    사용처
                  </span>
                  <span className="break-keep text-sm text-stone-600">
                    {couponDetail.placeOfExchange}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 선택형 상품 리스트 */}
        {isSelectable && choiceUnits.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-stone-800 px-2.5 py-0.5 text-base font-bold text-white">
                STEP 1
              </span>
              <h3 className="break-keep text-lg font-bold text-stone-700">
                원하시는 상품을 선택해 주세요
              </h3>
            </div>

            <div className="mt-3 space-y-2">
              {choiceUnits.map((unit) => (
                <button
                  key={unit._id}
                  onClick={() => handleSelectUnit(unit._id)}
                  className={`flex w-full items-center gap-3 rounded-lg border p-3 transition ${
                    selectedChoiceUnit === unit._id
                      ? 'border-stone-700 bg-stone-100'
                      : 'border-stone-200 bg-white'
                  }`}
                >
                  {unit.image?.url && (
                    <img
                      src={fileBucketUrl(unit.image.url, 'w200')}
                      alt={unit.name}
                      className="h-14 w-14 rounded object-cover"
                    />
                  )}
                  <div className="flex-1 text-left">
                    {unit.giftItemBrand?.name && (
                      <span className="mb-0.5 inline-block rounded-full border border-gray-400 bg-white px-2 py-0.5 text-xs font-medium text-stone-600">
                        {unit.giftItemBrand?.name}
                      </span>
                    )}
                    <p
                      className={`break-keep font-medium ${
                        selectedChoiceUnit === unit._id
                          ? 'text-stone-900'
                          : 'text-stone-700'
                      }`}
                    >
                      {unit.name}
                    </p>
                  </div>
                  {selectedChoiceUnit === unit._id && (
                    <CheckCircleSolidIcon className="h-7 w-7 text-stone-800" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 핸드폰 번호 입력 + 인증 */}
        <div ref={phoneInputRef}>
          <PhoneVerificationSection
            couponId={coupon._id}
            isSelectable={isSelectable}
            phoneNumber={phoneNumber}
            onPhoneNumberChange={setPhoneNumber}
            isVerified={isVerified}
            onVerified={() => setIsVerified(true)}
            onVerificationReset={() => {
              setIsVerified(false);
              setIsPreset(false);
            }}
            submitError={submitError}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
            isPreset={isPreset}
            verifiedAt={
              coupon.verification?.verifiedAt
                ? new Date(coupon.verification.verifiedAt)
                : undefined
            }
            additionalRecipientPhone={
              isExtraMode ? undefined : coupon.additionalRecipientPhone
            }
            isExtra={isExtraMode}
          />
        </div>

        {/* 유의사항 */}
        <div className="mt-4 rounded-lg bg-stone-100 p-3">
          <p className="text-xs font-medium text-stone-500">유의사항</p>
          <ul className="mt-1.5 space-y-1 text-xs text-stone-400">
            <li>
              • 교환권 신청 기한 및 상품 유효기간 내 미사용으로 인한 소멸은 본인에게
              책임이 있습니다.
            </li>
            <li>• 발송된 쿠폰은 해당 브랜드의 정책에 따라 사용 가능합니다.</li>
            <ExchangeCautionItems />
            <li>• 잘못된 번호 입력으로 인한 발송 오류는 재발송이 불가합니다.</li>
          </ul>

          <p className="mt-3 text-xs font-medium text-stone-500">개인정보 처리 안내</p>
          <ul className="mt-1.5 space-y-1 text-xs text-stone-400">
            <li>• 수집하는 개인정보: 수령인 휴대폰 번호</li>
            <li>• 수집 및 이용 목적: 답례품 전달 및 중복 발송 확인·관리</li>
            <li>
              • 위 목적 외에 개인정보를 일체 사용하지 않으며, 답례품 발송 완료 후 관련
              법령에 따라 보관 후 파기합니다.
            </li>
          </ul>
        </div>

        {/* 이용문의 */}
        <ContactEmailLink className="mt-4" />
      </div>

      <SignatureModal
        open={isSignatureModalOpen}
        onClose={() => setIsSignatureModalOpen(false)}
        onConfirm={handleSubmitWithSignature}
        isSubmitting={isSubmitting}
      />

      <ValidPeriodConsentModal
        open={isConsentModalOpen}
        validPeriod={couponDetail?.validPeriod}
        expiresAt={coupon.expiresAt ? new Date(coupon.expiresAt) : undefined}
        onConfirmNow={handleConsentNow}
        onConfirmLater={handleConsentLater}
        onClose={() => setIsConsentModalOpen(false)}
        isSavingLater={isSavingLater}
      />
    </div>
  );
};

export default GiftCouponRedeemPage;
